import React, { useState, useEffect } from 'react';
import { CheckSquare, Plus, Search, Calendar, AlertCircle, Check, Pencil, Trash2, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import CreateTaskModal from '../../../components/admin/crm/CreateTaskModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-800 border-red-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  low: 'bg-green-100 text-green-800 border-green-300'
};

const PRIORITY_LABELS = {
  high: 'High Priority',
  medium: 'Medium Priority',
  low: 'Low Priority'
};

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [completedFilter, setCompletedFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20 });
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0, overdue: 0 });
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchTasks();
    fetchCount();
    fetchStats();
  }, [pagination, searchTerm, completedFilter, priorityFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const completedParam = completedFilter ? `&completed=${completedFilter}` : '';
      const priorityParam = priorityFilter ? `&priority=${priorityFilter}` : '';
      
      const response = await fetch(
        `${API_URL}/api/crm/tasks?skip=${pagination.skip}&limit=${pagination.limit}${completedParam}${priorityParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch tasks');

      const data = await response.json();
      setTasks(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tasks',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const completedParam = completedFilter ? `?completed=${completedFilter}` : '';
      const priorityParam = priorityFilter ? (completedParam ? '&' : '?') + `priority=${priorityFilter}` : '';
      
      const response = await fetch(
        `${API_URL}/api/crm/tasks/count${completedParam}${priorityParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch count');

      const data = await response.json();
      setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/crm/tasks/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleComplete = async (taskId, completed) => {
    try {
      const endpoint = completed 
        ? `${API_URL}/api/crm/tasks/${taskId}`
        : `${API_URL}/api/crm/tasks/${taskId}/complete`;
      
      const method = completed ? 'PUT' : 'PATCH';
      const body = completed ? JSON.stringify({ completed: false }) : undefined;

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: body
      });

      if (!response.ok) throw new Error('Failed to update task');

      toast({
        title: 'Success',
        description: completed ? 'Task marked as pending' : 'Task completed'
      });
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: 'Error',
        description: 'Failed to update task',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await fetch(`${API_URL}/api/crm/tasks/${taskId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete task');

      toast({
        title: 'Success',
        description: 'Task deleted successfully'
      });
      fetchTasks();
      fetchCount();
      fetchStats();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete task',
        variant: 'destructive'
      });
    }
  };

  const handleRestore = async (taskId) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/tasks/${taskId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to restore task');

      toast({
        title: 'Success',
        description: 'Task restored successfully'
      });
      fetchTasks();
      fetchStats();
    } catch (error) {
      console.error('Error restoring task:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore task',
        variant: 'destructive'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dueDate, completed) => {
    if (!dueDate || completed) return false;
    return new Date(dueDate) < new Date();
  };

  const handleNextPage = () => {
    if (pagination.skip + pagination.limit < totalCount) {
      setPagination({ ...pagination, skip: pagination.skip + pagination.limit });
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) });
    }
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(totalCount / pagination.limit);

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <CheckSquare className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Tasks</h2>
              <p className="text-sm text-[#6C757D]">Manage your to-do list and action items</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Total Tasks</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Completed</p>
              <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Pending</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Overdue</p>
              <p className="text-3xl font-bold text-red-600">{stats.overdue}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Status Filter */}
            <select
              value={completedFilter}
              onChange={(e) => {
                setCompletedFilter(e.target.value);
                setPagination({ ...pagination, skip: 0 });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            >
              <option value="">All Tasks</option>
              <option value="false">Pending Only</option>
              <option value="true">Completed Only</option>
            </select>

            {/* Priority Filter */}
            <select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setPagination({ ...pagination, skip: 0 });
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            >
              <option value="">All Priorities</option>
              <option value="high">High Priority</option>
              <option value="medium">Medium Priority</option>
              <option value="low">Low Priority</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Tasks List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading tasks...</p>
          </CardContent>
        </Card>
      ) : tasks.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              {completedFilter || priorityFilter ? 'No tasks found matching your filters.' : 'No tasks yet. Add your first task!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <Card 
              key={task.id} 
              className={`${
                task.is_deleted ? 'opacity-50' : ''
              } ${
                isOverdue(task.due_date, task.completed) ? 'border-2 border-red-300' : ''
              }`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  {/* Checkbox */}
                  {!task.is_deleted && (
                    <button
                      onClick={() => handleComplete(task.id, task.completed)}
                      className={`mt-1 flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center ${
                        task.completed
                          ? 'bg-green-500 border-green-500'
                          : 'border-gray-300 hover:border-[#00B4D8]'
                      }`}
                    >
                      {task.completed && <Check className="h-4 w-4 text-white" />}
                    </button>
                  )}

                  {/* Task Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className={`text-lg font-semibold ${
                            task.completed ? 'line-through text-[#6C757D]' : 'text-[#2B2D42]'
                          }`}>
                            {task.subject}
                          </h3>
                          <span className={`px-2 py-1 text-xs rounded-full border ${PRIORITY_COLORS[task.priority]}`}>
                            {PRIORITY_LABELS[task.priority]}
                          </span>
                          {task.is_deleted && (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                              Deleted
                            </span>
                          )}
                          {isOverdue(task.due_date, task.completed) && (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full flex items-center space-x-1">
                              <AlertCircle className="h-3 w-3" />
                              <span>Overdue</span>
                            </span>
                          )}
                        </div>
                        {task.description && (
                          <p className="text-sm text-[#6C757D] mb-2">{task.description}</p>
                        )}
                        <div className="flex flex-wrap gap-3 text-sm text-[#6C757D]">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>{formatDate(task.due_date)}</span>
                          </div>
                          {task.assigned_to && (
                            <div>
                              <span className="font-medium">Assigned:</span> {task.assigned_to}
                            </div>
                          )}
                          <div>
                            <span className="font-medium">Related to:</span> {task.related_to_type}
                          </div>
                        </div>
                        {task.tags && task.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {task.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-0.5 text-xs bg-[#E0F2FE] text-[#00B4D8] rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        {task.is_deleted ? (
                          <Button
                            onClick={() => handleRestore(task.id)}
                            variant="outline"
                            size="sm"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Restore
                          </Button>
                        ) : (
                          <Button
                            onClick={() => handleDelete(task.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > pagination.limit && (
        <div className="mt-6 flex items-center justify-center space-x-4">
          <Button
            onClick={handlePrevPage}
            disabled={pagination.skip === 0}
            variant="outline"
          >
            Previous
          </Button>
          <span className="text-sm text-[#6C757D]">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={pagination.skip + pagination.limit >= totalCount}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Modal */}
      <CreateTaskModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          fetchTasks();
          fetchCount();
          fetchStats();
        }}
      />
    </div>
  );
};

export default Tasks;